import type { ReactNode } from 'react';
import { Navigate, Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import { AuthProvider, useAuth } from '@core/auth/AuthProvider';
import AuthGate from '@core/auth/AuthGate';
import AppLayout from '@core/components/AppLayout';
import { RouteErrorScreen } from '@core/components/AppErrorBoundary';
import { ToastProvider } from '@core/components/Toast';
import RequireRole from '@core/components/RequireRole';
import OperatorsPage from '@core/masters/OperatorsPage';
import CompaniesPage from '@core/masters/CompaniesPage';
import CsvImportPage from '@core/masters/CsvImportPage';
import BbCredentialsPage from '@core/masters/BbCredentialsPage';
import SettingsUsersPage from '@core/masters/SettingsUsersPage';
import SettingsSystemPage from '@core/masters/SettingsSystemPage';
import SettingsMonthlyPage from '@core/masters/SettingsMonthlyPage';
import SettingsPayPage from '@core/masters/SettingsPayPage';
import SettingsDeliveryPlanPage from '@core/masters/SettingsDeliveryPlanPage';
import CloseDayPage from '@core/dashboard/CloseDayPage';
import BbApiPage from '@core/masters/BbApiPage';
import HomePage from '@core/dashboard/HomePage';
import StaffMyPage from '@core/staff/StaffMyPage';
import ShiftPage from '@core/staff/ShiftPage';
import QaPage from '@core/qa/QaPage';
import TrainingPage from '@core/training/TrainingPage';
import PlPage from '@core/dashboard/PlPage';
import AnalysisPage from '@core/dashboard/AnalysisPage';
import DeliveryInputPage from '@core/dashboard/DeliveryInputPage';
import CompaniesSummaryPage from '@core/dashboard/CompaniesSummaryPage';
import CloseMonthPage from '@core/dashboard/CloseMonthPage';
import MonthlyStaffPage from '@core/dashboard/MonthlyStaffPage';
import StylesSummaryPage from '@core/dashboard/StylesSummaryPage';
import StaffSummaryPage from '@core/dashboard/StaffSummaryPage';
import DesignPreviewPage from '@core/theme/DesignPreviewPage';
import { ThemeProvider } from '@core/theme/ThemeProvider';
import type { Role } from '@core/lib/types';

/** ロール別リダイレクト(§3): admin=マスタ / viewer=月次 / operator=本人成績 */
function HomeRedirect() {
  const { homePath } = useAuth();
  return <Navigate to={homePath} replace />;
}

/** 画面ごとの許可ロール(firestore.rules と同じ条件を UI 側にも) */
function Guard({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { role } = useAuth();
  return <RequireRole allowed={role !== null && roles.includes(role)}>{children}</RequireRole>;
}

const ADMIN: Role[] = ['admin'];
const ADMIN_VIEWER: Role[] = ['admin', 'viewer'];
const ADMIN_OPERATOR: Role[] = ['admin', 'operator'];

/**
 * データルーター(createBrowserRouter)。未保存の変更があるページの離脱確認(useBlocker)に必要。
 * ログイン状態の出し分け(AuthGate)はレイアウトルートの中で行う
 */
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      element={
        <AuthGate>
          <AppLayout />
        </AuthGate>
      }
      errorElement={<RouteErrorScreen />}
    >
      <Route index element={<HomeRedirect />} />

      <Route
        path="/home"
        element={
          <Guard roles={ADMIN}>
            <HomePage />
          </Guard>
        }
      />

      <Route
        path="/operators"
        element={
          <Guard roles={ADMIN_VIEWER}>
            <OperatorsPage />
          </Guard>
        }
      />
      <Route
        path="/companies"
        element={
          <Guard roles={ADMIN_VIEWER}>
            <CompaniesPage />
          </Guard>
        }
      />
      <Route
        path="/import"
        element={
          <Guard roles={ADMIN}>
            <CsvImportPage />
          </Guard>
        }
      />
      <Route
        path="/bb-credentials"
        element={
          <Guard roles={ADMIN}>
            <BbCredentialsPage />
          </Guard>
        }
      />

      {/* ---- 集計(⑥指示: PL / 会社別 / 出勤形態×報酬区分別 / スタッフ別成績。各ページ内で日次/週次/月次を切替) ---- */}
      <Route
        path="/pl"
        element={
          <Guard roles={ADMIN_VIEWER}>
            <PlPage />
          </Guard>
        }
      />
      {/* 売上実績(納品)ページは廃止し、日報/サマリ に統合(2026-09-10 指示) */}
      <Route path="/summary/sales" element={<Navigate to="/summary/deliveries" replace />} />
      <Route
        path="/summary/deliveries"
        element={
          <Guard roles={ADMIN}>
            <DeliveryInputPage />
          </Guard>
        }
      />
      <Route
        path="/summary/companies"
        element={
          <Guard roles={ADMIN_VIEWER}>
            <CompaniesSummaryPage />
          </Guard>
        }
      />
      <Route
        path="/summary/styles"
        element={
          <Guard roles={ADMIN_VIEWER}>
            <StylesSummaryPage />
          </Guard>
        }
      />
      <Route
        path="/summary/staff"
        element={
          <Guard roles={ADMIN_VIEWER}>
            <StaffSummaryPage />
          </Guard>
        }
      />
      {/* 分析(2026-09-20 発注者指示。P-17) */}
      {/* 月締め作業(2026-09-24 発注者指示。P-67) */}
      <Route
        path="/close-month"
        element={
          <Guard roles={ADMIN}>
            <CloseMonthPage />
          </Guard>
        }
      />
      {/* 月次【スタッフ別】(2026-09-24 発注者指示。P-64) */}
      <Route
        path="/summary/monthly-staff"
        element={
          <Guard roles={ADMIN_VIEWER}>
            <MonthlyStaffPage />
          </Guard>
        }
      />
      <Route
        path="/analysis"
        element={
          <Guard roles={ADMIN_VIEWER}>
            <AnalysisPage />
          </Guard>
        }
      />
      {/* 旧URL */}
      <Route path="/daily" element={<Navigate to="/pl?mode=day" replace />} />
      <Route path="/monthly" element={<Navigate to="/pl?mode=month" replace />} />

      {/* ---- プレースホルダ(後続Phaseで実装) ---- */}
      <Route
        path="/staff"
        element={
          <Guard roles={ADMIN_OPERATOR}>
            <StaffMyPage />
          </Guard>
        }
      />
      <Route
        path="/shift"
        element={
          <Guard roles={ADMIN_OPERATOR}>
            <ShiftPage />
          </Guard>
        }
      />
      {/* トレーニング経過(2026-10-08。D-197)。面談の記録は個人の評価なので admin だけ */}
      <Route
        path="/training"
        element={
          <Guard roles={ADMIN}>
            <TrainingPage />
          </Guard>
        }
      />
      <Route
        path="/qa"
        element={
          <Guard roles={ADMIN_OPERATOR}>
            <QaPage />
          </Guard>
        }
      />

      <Route
        path="/settings/users"
        element={
          <Guard roles={ADMIN}>
            <SettingsUsersPage />
          </Guard>
        }
      />
      {/* 日次締め作業(①納品件数→②人件費→③交通費→④出勤表。2026-09-24 D-147) */}
      <Route
        path="/close"
        element={
          <Guard roles={ADMIN}>
            <CloseDayPage />
          </Guard>
        }
      />
      {/* 月次設定(目標・前提値)。設定メニューから開く(2026-09-24 発注者指示) */}
      <Route
        path="/settings/monthly"
        element={
          <Guard roles={ADMIN}>
            <SettingsMonthlyPage />
          </Guard>
        }
      />
      {/* 納品件数の予定(曜日別・まとめて登録。2026-09-24 D-145) */}
      <Route
        path="/settings/delivery-plan"
        element={
          <Guard roles={ADMIN}>
            <SettingsDeliveryPlanPage />
          </Guard>
        }
      />
      {/* 報酬・通話料の設定(インセンティブ・発信番号。2026-09-24) */}
      <Route
        path="/settings/pay"
        element={
          <Guard roles={ADMIN}>
            <SettingsPayPage />
          </Guard>
        }
      />
      <Route
        path="/settings/system"
        element={
          <Guard roles={ADMIN}>
            <SettingsSystemPage />
          </Guard>
        }
      />
      <Route
        path="/settings/bluebean-api"
        element={
          <Guard roles={ADMIN}>
            <BbApiPage />
          </Guard>
        }
      />
      {/* デザイン比較(D-132)。4つのテーマを実際の画面(読み取り専用)で見比べる。管理者だけ */}
      <Route
        path="/design-preview"
        element={
          <Guard roles={ADMIN}>
            <DesignPreviewPage />
          </Guard>
        }
      />

      <Route path="*" element={<HomeRedirect />} />
    </Route>,
  ),
);

export default function App() {
  return (
    <AuthProvider>
      {/* テーマ(D-132)はログインしている人ごとに決まるので、認証の内側に置く */}
      <ThemeProvider>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}
